import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';
import DynamicSidebar from '../../components/ui/DynamicSidebar';
import DashboardHeader from '../../components/ui/DashboardHeader';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../context/AuthContext';

const InputField = ({ label, name, type = 'text', placeholder, formData, formErrors, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-text-primary mb-1">{label}</label>
    <input
      type={type}
      name={name}
      value={formData?.[name]}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
        formErrors?.[name] ? 'border-error focus:ring-error/30' : 'border-border'
      }`}
    />
    {formErrors?.[name] && (
      <p className="mt-1 text-xs text-error flex items-center space-x-1">
        <Icon name="AlertCircle" size={12} />
        <span>{formErrors?.[name]}</span>
      </p>
    )}
  </div>
);

const UsersPage = () => {
  const { user: loggedInUser, businesses, businessCode } = useAuth();
  const loggedInUserTypeId = String(loggedInUser?.user_type?.id || '');
  const loggedInUserTypeName = String(loggedInUser?.user_type?.type || '');
  const isStaff = loggedInUserTypeId === '3' || loggedInUserTypeName?.toLowerCase() === 'staff';

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Column filters
  const [filters, setFilters] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    status: '1',
    user_type: '',
  });

  // Portfolio modal state
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [portfolioUser, setPortfolioUser] = useState(null);
  const [portfolioData, setPortfolioData] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(false);
  const [portfolioError, setPortfolioError] = useState('');

  // User types for dropdown
  const [userTypes, setUserTypes] = useState([]);
  const [userTypesLoading, setUserTypesLoading] = useState(false);

  // Create form state
  const [formData, setFormData] = useState({
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    user_type: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [apiErrors, setApiErrors] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Staff action modal state
  const [actionModal, setActionModal] = useState({ open: false, type: null, user: null });
  const [actionLoading, setActionLoading] = useState(false);
  const [actionResult, setActionResult] = useState({ success: null, message: '' });
  const [actionOtpStep, setActionOtpStep] = useState(false);
  const [actionOtp, setActionOtp] = useState('');
  const [actionOtpError, setActionOtpError] = useState('');
  const [resetPasswordData, setResetPasswordData] = useState({ new_password: '', confirm_password: '' });
  const [resetPasswordErrors, setResetPasswordErrors] = useState({});

  const getStatusBadge = (status) => {
    const s = Number(status);
    if (s === 0) return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error/10 text-error border border-error/20">
        DISABLED
      </span>
    );
    if (s === 1) return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
        ACTIVE
      </span>
    );
    if (s === 2) return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20">
        LOCKED
      </span>
    );
    return <span className="text-xs text-text-secondary">—</span>;
  };

  const filteredUsers = users?.filter(user => {
    const matchFirst = !filters?.first_name || user?.first_name?.toLowerCase()?.includes(filters?.first_name?.toLowerCase());
    const matchLast = !filters?.last_name || user?.last_name?.toLowerCase()?.includes(filters?.last_name?.toLowerCase());
    const matchEmail = !filters?.email || user?.email?.toLowerCase()?.includes(filters?.email?.toLowerCase());
    const matchPhone = !filters?.phone || String(user?.phone || '')?.toLowerCase()?.includes(filters?.phone?.toLowerCase());
    const matchStatus = filters?.status === '' || String(user?.status) === filters?.status;
    const matchType = !filters?.user_type || user?.user_type?.user_type?.toLowerCase()?.includes(filters?.user_type?.toLowerCase());
    return matchFirst && matchLast && matchEmail && matchPhone && matchStatus && matchType;
  });

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const userType = loggedInUser?.user_type?.id;
      const response = await apiService?.getUsers(businesses, userType);
      const data = response?.data;
      setUsers(data?.details || []);
    } catch (err) {
      setError(err?.response?.data?.status_description || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserTypes = async () => {
    setUserTypesLoading(true);
    try {
      const response = await apiService?.get('/portal/users/types');
      const data = response?.data;
      const allTypes = data?.details || [];

      if (isStaff) {
        setUserTypes(allTypes);
      } else {
        const filtered = allTypes?.filter(type => String(type?.id) === loggedInUserTypeId);
        setUserTypes(filtered);
      }
    } catch (err) {
      setUserTypes([]);
    } finally {
      setUserTypesLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    fetchUserTypes();
    setShowCreateModal(true);
  };

  const handleViewPortfolio = async (user) => {
    setPortfolioUser(user);
    setPortfolioData([]);
    setPortfolioError('');
    setShowPortfolioModal(true);
    setPortfolioLoading(true);
    try {
      const response = await apiService?.get(`/portal/portfolio/${user?.user_id}/user`);
      const data = response?.data;
      const details = data?.details || [];
      const grouped = {};
      details?.forEach(item => {
        const key = item?.business_name || item?.business_code || 'Unknown Business';
        if (!grouped?.[key]) {
          grouped[key] = {
            business_name: key,
            business_code: item?.business_code,
            merchant_ids: [],
          };
        }
        if (item?.merchant_id) {
          grouped?.[key]?.merchant_ids?.push(item?.merchant_id);
        }
      });
      setPortfolioData(Object.values(grouped));
    } catch (err) {
      setPortfolioError(err?.response?.data?.status_description || 'Failed to load portfolio');
    } finally {
      setPortfolioLoading(false);
    }
  };

  const handleClosePortfolioModal = () => {
    setShowPortfolioModal(false);
    setPortfolioUser(null);
    setPortfolioData([]);
    setPortfolioError('');
  };

  const validateForm = () => {
    const errors = {};
    if (!formData?.first_name || formData?.first_name?.length < 2 || formData?.first_name?.length > 100)
      errors.first_name = 'First name must be between 2 and 100 characters';
    if (!formData?.middle_name || formData?.middle_name?.length < 2 || formData?.middle_name?.length > 100)
      errors.middle_name = 'Middle name must be between 2 and 100 characters';
    if (!formData?.last_name || formData?.last_name?.length < 2 || formData?.last_name?.length > 100)
      errors.last_name = 'Last name must be between 2 and 100 characters';
    if (!formData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email))
      errors.email = 'Please enter a valid email address';
    if (!formData?.phone_number || !/^\d{10,15}$/?.test(formData?.phone_number))
      errors.phone_number = 'Phone number must be between 10 and 15 digits';
    if (!formData?.user_type) errors.user_type = 'User type is required';
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors?.[name]) setFormErrors(prev => ({ ...prev, [name]: '' }));
    setApiErrors([]);
  };

  const handleCreateUser = async (e) => {
    e?.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors)?.length > 0) { setFormErrors(errors); return; }

    setCreateLoading(true);
    setApiErrors([]);
    setSuccessMessage('');
    try {
      const payload = {
        first_name: formData?.first_name,
        middle_name: formData?.middle_name,
        last_name: formData?.last_name,
        email: formData?.email,
        phone_number: formData?.phone_number,
        user_type: parseInt(formData?.user_type, 10),
        business_code: businessCode,
        parent_user_type: parseInt(loggedInUserTypeId, 10),
      };
      const response = await apiService?.post('/portal/users', payload);
      const data = response?.data;
      if (data?.status_code === 200) {
        setSuccessMessage('User created successfully!');
        setFormData({ first_name: '', middle_name: '', last_name: '', email: '', phone_number: '', user_type: '' });
        setFormErrors({});
        await fetchUsers();
        setTimeout(() => { setShowCreateModal(false); setSuccessMessage(''); }, 2000);
      }
    } catch (err) {
      const errData = err?.response?.data;
      if (errData?.status_code === 400 && Array.isArray(errData?.details)) {
        setApiErrors(errData?.details);
      } else {
        setApiErrors([errData?.status_description || 'Failed to create user']);
      }
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowCreateModal(false);
    setFormData({ first_name: '', middle_name: '', last_name: '', email: '', phone_number: '', user_type: '' });
    setFormErrors({});
    setApiErrors([]);
    setSuccessMessage('');
  };

  // Staff actions
  const openActionModal = (type, user) => {
    setActionModal({ open: true, type, user });
    setActionResult({ success: null, message: '' });
    setActionOtpStep(false);
    setActionOtp('');
    setActionOtpError('');
  };

  const closeActionModal = () => {
    setActionModal({ open: false, type: null, user: null });
    setActionResult({ success: null, message: '' });
    setActionOtpStep(false);
    setActionOtp('');
    setActionOtpError('');
  };

  const handleProceedToOtp = () => {
    setActionOtpStep(true);
    setActionOtp('');
    setActionOtpError('');
  };

  const handleResetPassword = async () => {
    if (!actionOtp || actionOtp?.trim()?.length === 0) {
      setActionOtpError('Please enter your OTP.');
      return;
    }
    setActionLoading(true);
    setActionOtpError('');
    setActionResult({ success: null, message: '' });
    try {
      const emailAddress = loggedInUser?.email_address || loggedInUser?.email || '';
      const verifyResponse = await apiService?.verify2FA({ email_address: emailAddress, code: actionOtp?.trim() });
      const verifyData = verifyResponse?.data;
      const verified = verifyData?.details?.verified;
      if (verifyData?.status_code === 200 && verified === true) {
        const userId = actionModal?.user?.user_id;
        const response = await apiService?.put(`/portal/users/${userId}/password/reset`, {});
        const data = response?.data;
        setActionResult({ success: true, message: 'Password reset successful' });
      } else {
        setActionOtpError(verifyData?.status_description || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setActionOtpError(err?.response?.data?.status_description || 'OTP verification failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetOtp = async () => {
    if (!actionOtp || actionOtp?.trim()?.length === 0) {
      setActionOtpError('Please enter your OTP.');
      return;
    }
    setActionLoading(true);
    setActionOtpError('');
    setActionResult({ success: null, message: '' });
    try {
      const emailAddress = loggedInUser?.email_address || loggedInUser?.email || '';
      const verifyResponse = await apiService?.verify2FA({ email_address: emailAddress, code: actionOtp?.trim() });
      const verifyData = verifyResponse?.data;
      const verified = verifyData?.details?.verified;
      if (verifyData?.status_code === 200 && verified === true) {
        const userId = actionModal?.user?.user_id;
        const response = await apiService?.put(`/portal/users/${userId}/reset-otp`, {});
        const data = response?.data;
        setActionResult({ success: true, message: 'OTP reset successful' });
      } else {
        setActionOtpError(verifyData?.status_description || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setActionOtpError(err?.response?.data?.status_description || 'OTP verification failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisableUser = async () => {
    if (!actionOtp || actionOtp?.trim()?.length === 0) {
      setActionOtpError('Please enter your OTP.');
      return;
    }
    setActionLoading(true);
    setActionOtpError('');
    setActionResult({ success: null, message: '' });
    try {
      const emailAddress = loggedInUser?.email_address || loggedInUser?.email || '';
      const verifyResponse = await apiService?.verify2FA({ email_address: emailAddress, code: actionOtp?.trim() });
      const verifyData = verifyResponse?.data;
      const verified = verifyData?.details?.verified;
      if (verifyData?.status_code === 200 && verified === true) {
        const userId = actionModal?.user?.user_id;
        const response = await apiService?.put(`/portal/users/${userId}/disable`, {});
        const data = response?.data;
        setActionResult({ success: true, message: 'User disabled successfully' });
        await fetchUsers();
      } else {
        setActionOtpError(verifyData?.status_description || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setActionOtpError(err?.response?.data?.status_description || 'OTP verification failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlockUser = async () => {
    if (!actionOtp || actionOtp?.trim()?.length === 0) {
      setActionOtpError('Please enter your OTP.');
      return;
    }
    setActionLoading(true);
    setActionOtpError('');
    setActionResult({ success: null, message: '' });
    try {
      const emailAddress = loggedInUser?.email_address || loggedInUser?.email || '';
      const verifyResponse = await apiService?.verify2FA({ email_address: emailAddress, code: actionOtp?.trim() });
      const verifyData = verifyResponse?.data;
      const verified = verifyData?.details?.verified;
      if (verifyData?.status_code === 200 && verified === true) {
        const userId = actionModal?.user?.user_id;
        const response = await apiService?.put(`/portal/users/${userId}/unlock`, {});
        const data = response?.data;
        setActionResult({ success: true, message: 'Account unlocked successfully' });
        await fetchUsers();
      } else {
        setActionOtpError(verifyData?.status_description || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setActionOtpError(err?.response?.data?.status_description || 'OTP verification failed.');
    } finally {
      setActionLoading(false);
    }
  };

  const getActionConfig = (type) => {
    switch (type) {
      case 'reset-password':
        return { title: 'Reset Password', icon: 'KeyRound', iconColor: 'text-warning', iconBg: 'bg-warning/10' };
      case 'reset-otp':
        return { title: 'Reset OTP', icon: 'ShieldOff', iconColor: 'text-accent', iconBg: 'bg-accent/10' };
      case 'disable-user':
        return { title: 'Disable User', icon: 'UserX', iconColor: 'text-error', iconBg: 'bg-error/10' };
      case 'unlock-user':
        return { title: 'Unlock Account', icon: 'LockOpen', iconColor: 'text-success', iconBg: 'bg-success/10' };
      default:
        return { title: '', icon: 'Info', iconColor: 'text-primary', iconBg: 'bg-primary/10' };
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader onMenuToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <DynamicSidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-semibold text-text-primary">Users</h1>
              <p className="text-text-secondary mt-1">
                Manage system users
                {users?.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {users?.length} user{users?.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button onClick={fetchUsers} className="flex items-center space-x-2 px-4 py-2 border border-border rounded-lg text-sm hover:bg-muted">
                <Icon name="RefreshCw" size={16} className="text-text-secondary" />
                <span>Refresh</span>
              </button>
              <button
                onClick={handleOpenCreateModal}
                className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90"
              >
                <Icon name="UserPlus" size={16} />
                <span>Create User</span>
              </button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="AlertCircle" size={16} className="text-error" />
              <p className="text-sm text-error">{error}</p>
            </div>
          )}

          {/* Users Table */}
          <div className="bg-surface border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/50">
                    {['User ID', 'First Name', 'Last Name', 'Email', 'Phone', 'User Type', 'Status', 'Actions']?.map(col => (
                      <th key={col} className="text-left px-4 py-3 text-xs font-semibold text-text-secondary uppercase tracking-wide whitespace-nowrap">{col}</th>
                    ))}
                  </tr>
                  {/* Filter Row */}
                  <tr className="border-b border-border bg-muted/20">
                    {/* User ID — no filter */}
                    <td className="px-4 py-2"></td>
                    {/* First Name filter */}
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={filters?.first_name}
                        onChange={e => handleFilterChange('first_name', e?.target?.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    {/* Last Name filter */}
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={filters?.last_name}
                        onChange={e => handleFilterChange('last_name', e?.target?.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    {/* Email filter */}
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={filters?.email}
                        onChange={e => handleFilterChange('email', e?.target?.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    {/* Phone filter */}
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={filters?.phone}
                        onChange={e => handleFilterChange('phone', e?.target?.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    {/* User Type filter */}
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={filters?.user_type}
                        onChange={e => handleFilterChange('user_type', e?.target?.value)}
                        placeholder="Filter..."
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </td>
                    {/* Status filter */}
                    <td className="px-4 py-2">
                      <select
                        value={filters?.status}
                        onChange={e => handleFilterChange('status', e?.target?.value)}
                        className="w-full px-2 py-1 text-xs border border-border rounded bg-background text-text-primary focus:outline-none focus:ring-1 focus:ring-primary/30"
                      >
                        <option value="">All</option>
                        <option value="1">ACTIVE</option>
                        <option value="0">DISABLED</option>
                        <option value="2">LOCKED</option>
                      </select>
                    </td>
                    {/* Actions — no filter */}
                    <td className="px-4 py-2"></td>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                          <p className="text-sm text-text-secondary">Loading users...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredUsers?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center">
                        <div className="flex flex-col items-center space-y-2">
                          <Icon name="Users" size={32} className="text-text-secondary" />
                          <p className="text-sm text-text-secondary">{users?.length === 0 ? 'No users found' : 'No users match the current filters'}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers?.map((user, idx) => (
                      <tr key={user?.id || idx} className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono bg-primary/10 text-primary">{user?.user_id}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-text-primary">{user?.first_name}</td>
                        <td className="px-4 py-3 text-sm text-text-primary">{user?.last_name}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{user?.email}</td>
                        <td className="px-4 py-3 text-sm text-text-secondary">{user?.phone}</td>
                        <td className="px-4 py-3">
                          {user?.user_type?.user_type ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                              {user?.user_type?.user_type}
                            </span>
                          ) : (
                            <span className="text-xs text-text-secondary">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(user?.status)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2 flex-wrap gap-y-1">
                            <button
                              onClick={() => handleViewPortfolio(user)}
                              className="inline-flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 border border-primary/20 rounded-lg transition-colors"
                            >
                              <Icon name="Eye" size={13} />
                              <span>View</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      {/* Portfolio / View User Modal */}
      {showPortfolioModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleClosePortfolioModal} />
          <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="User" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-text-primary">User Details</h2>
                  <p className="text-xs text-text-secondary">
                    {portfolioUser?.first_name} {portfolioUser?.last_name}
                    {portfolioUser?.user_id && (
                      <span className="ml-1 font-mono text-primary">({portfolioUser?.user_id})</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={handleClosePortfolioModal} className="p-1.5 rounded-lg hover:bg-muted text-text-secondary">
                <Icon name="X" size={18} />
              </button>
            </div>

            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">Full Name</p>
                  <p className="text-sm font-medium text-text-primary">{portfolioUser?.first_name} {portfolioUser?.last_name}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">User ID</p>
                  <p className="text-sm font-mono text-primary">{portfolioUser?.user_id || '—'}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">Email</p>
                  <p className="text-sm text-text-primary break-all">{portfolioUser?.email || '—'}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">Phone</p>
                  <p className="text-sm text-text-primary">{portfolioUser?.phone || '—'}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">User Type</p>
                  <p className="text-sm text-text-primary">{portfolioUser?.user_type?.user_type || '—'}</p>
                </div>
                <div className="bg-muted/40 rounded-lg px-4 py-3">
                  <p className="text-xs text-text-secondary mb-0.5">Status</p>
                  <div className="mt-0.5">{getStatusBadge(portfolioUser?.status)}</div>
                </div>
              </div>

              {/* Staff Actions */}
              {isStaff && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="px-4 py-3 bg-muted/40 border-b border-border">
                    <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Actions</p>
                  </div>
                  <div className="p-4 flex flex-wrap gap-2">
                    {Number(portfolioUser?.status) === 1 && (
                      <>
                        <button
                          onClick={() => { handleClosePortfolioModal(); openActionModal('reset-password', portfolioUser); }}
                          className="inline-flex items-center space-x-1.5 px-3 py-2 text-xs font-medium text-warning bg-warning/10 hover:bg-warning/20 border border-warning/20 rounded-lg transition-colors"
                        >
                          <Icon name="KeyRound" size={13} />
                          <span>Reset Password</span>
                        </button>
                        <button
                          onClick={() => { handleClosePortfolioModal(); openActionModal('reset-otp', portfolioUser); }}
                          className="inline-flex items-center space-x-1.5 px-3 py-2 text-xs font-medium text-accent bg-accent/10 hover:bg-accent/20 border border-accent/20 rounded-lg transition-colors"
                        >
                          <Icon name="ShieldOff" size={13} />
                          <span>Reset OTP</span>
                        </button>
                        <button
                          onClick={() => { handleClosePortfolioModal(); openActionModal('disable-user', portfolioUser); }}
                          className="inline-flex items-center space-x-1.5 px-3 py-2 text-xs font-medium text-error bg-error/10 hover:bg-error/20 border border-error/20 rounded-lg transition-colors"
                        >
                          <Icon name="UserX" size={13} />
                          <span>Disable</span>
                        </button>
                      </>
                    )}
                    {Number(portfolioUser?.status) === 2 && (
                      <>
                        <button
                          onClick={() => { handleClosePortfolioModal(); openActionModal('unlock-user', portfolioUser); }}
                          className="inline-flex items-center space-x-1.5 px-3 py-2 text-xs font-medium text-success bg-success/10 hover:bg-success/20 border border-success/20 rounded-lg transition-colors"
                        >
                          <Icon name="LockOpen" size={13} />
                          <span>Unlock</span>
                        </button>
                        <button
                          onClick={() => { handleClosePortfolioModal(); openActionModal('disable-user', portfolioUser); }}
                          className="inline-flex items-center space-x-1.5 px-3 py-2 text-xs font-medium text-error bg-error/10 hover:bg-error/20 border border-error/20 rounded-lg transition-colors"
                        >
                          <Icon name="UserX" size={13} />
                          <span>Disable</span>
                        </button>
                      </>
                    )}
                    {Number(portfolioUser?.status) === 0 && (
                      <p className="text-xs text-text-secondary italic">No actions available for disabled accounts.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Business Access */}
              <div>
                <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-3">Business Access</p>
                {portfolioLoading ? (
                  <div className="flex flex-col items-center justify-center py-10 space-y-3">
                    <Icon name="Loader2" size={28} className="text-primary animate-spin" />
                    <p className="text-sm text-text-secondary">Loading portfolio...</p>
                  </div>
                ) : portfolioError ? (
                  <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                    <p className="text-sm text-error">{portfolioError}</p>
                  </div>
                ) : portfolioData?.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 space-y-3">
                    <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                      <Icon name="Building2" size={24} className="text-text-secondary" />
                    </div>
                    <p className="text-sm font-medium text-text-primary">No Assigned Businesses</p>
                    <p className="text-xs text-text-secondary text-center">This user has no assigned businesses or merchants.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-xs text-text-secondary mb-3">
                      {portfolioData?.length} business{portfolioData?.length !== 1 ? 'es' : ''} assigned
                    </p>
                    {portfolioData?.map((business, idx) => (
                      <div key={idx} className="border border-border rounded-lg overflow-hidden">
                        <div className="flex items-center space-x-3 px-4 py-3 bg-muted/40">
                          <div className="w-7 h-7 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0">
                            <Icon name="Building2" size={14} className="text-primary" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-text-primary truncate">{business?.business_name}</p>
                            {business?.business_code && (
                              <p className="text-xs font-mono text-text-secondary">{business?.business_code}</p>
                            )}
                          </div>
                        </div>
                        <div className="px-4 py-3">
                          {business?.merchant_ids?.length > 0 ? (
                            <div>
                              <p className="text-xs font-medium text-text-secondary uppercase tracking-wide mb-2">
                                Merchants ({business?.merchant_ids?.length})
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {business?.merchant_ids?.map((mid, mIdx) => (
                                  <span
                                    key={mIdx}
                                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-mono bg-accent/10 text-accent border border-accent/20"
                                  >
                                    <Icon name="Store" size={11} className="mr-1.5" />
                                    {mid}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="text-xs text-text-secondary italic">No merchants assigned</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end px-5 py-4 border-t border-border flex-shrink-0">
              <button
                onClick={handleClosePortfolioModal}
                className="px-4 py-2 border border-border rounded-lg text-sm text-text-primary hover:bg-muted"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Staff Action Modal (Reset Password / Reset OTP / Disable User) */}
      {actionModal?.open && (() => {
        const config = getActionConfig(actionModal?.type);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={!actionLoading ? closeActionModal : undefined} />
            <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-md">
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-border">
                <div className="flex items-center space-x-3">
                  <div className={`w-9 h-9 ${config?.iconBg} rounded-lg flex items-center justify-center`}>
                    <Icon name={config?.icon} size={18} className={config?.iconColor} />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-text-primary">{config?.title}</h2>
                    <p className="text-xs text-text-secondary">
                      {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      {actionModal?.user?.user_id && (
                        <span className="ml-1 font-mono text-primary">({actionModal?.user?.user_id})</span>
                      )}
                    </p>
                  </div>
                </div>
                {!actionLoading && (
                  <button onClick={closeActionModal} className="p-1.5 rounded-lg hover:bg-muted text-text-secondary">
                    <Icon name="X" size={18} />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                {/* Result feedback */}
                {actionResult?.success === true && (
                  <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                    <p className="text-sm text-success">{actionResult?.message}</p>
                  </div>
                )}
                {actionResult?.success === false && (
                  <div className="flex items-center space-x-2 p-3 bg-error/10 border border-error/20 rounded-lg">
                    <Icon name="AlertCircle" size={16} className="text-error flex-shrink-0" />
                    <p className="text-sm text-error">{actionResult?.message}</p>
                  </div>
                )}

                {/* Reset Password — OTP only (no new/confirm password) */}
                {actionModal?.type === 'reset-password' && actionResult?.success !== true && (
                  <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                      Enter the OTP from authenticator to confirm resetting the password for{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">OTP Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={actionOtp}
                        onChange={(e) => { setActionOtp(e?.target?.value); setActionOtpError(''); }}
                        placeholder="Enter OTP"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          actionOtpError ? 'border-error focus:ring-error/30' : 'border-border'
                        }`}
                      />
                      {actionOtpError && (
                        <p className="mt-1 text-xs text-error flex items-center space-x-1">
                          <Icon name="AlertCircle" size={12} />
                          <span>{actionOtpError}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Reset OTP — confirmation step */}
                {actionModal?.type === 'reset-otp' && actionResult?.success !== true && !actionOtpStep && (
                  <div className="flex items-start space-x-3 p-3 bg-accent/5 border border-accent/20 rounded-lg">
                    <Icon name="Info" size={16} className="text-accent flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-text-secondary">
                      This will reset the OTP/authentication credentials for{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>. The user will need to set up their OTP again on next login.
                    </p>
                  </div>
                )}

                {/* Reset OTP — OTP step */}
                {actionModal?.type === 'reset-otp' && actionResult?.success !== true && actionOtpStep && (
                  <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                      Enter the OTP from authenticator to confirm resetting OTP for{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">OTP Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={actionOtp}
                        onChange={(e) => { setActionOtp(e?.target?.value); setActionOtpError(''); }}
                        placeholder="Enter OTP"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          actionOtpError ? 'border-error focus:ring-error/30' : 'border-border'
                        }`}
                      />
                      {actionOtpError && (
                        <p className="mt-1 text-xs text-error flex items-center space-x-1">
                          <Icon name="AlertCircle" size={12} />
                          <span>{actionOtpError}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Disable User — confirmation step */}
                {actionModal?.type === 'disable-user' && actionResult?.success !== true && !actionOtpStep && (
                  <div className="flex items-start space-x-3 p-3 bg-error/5 border border-error/20 rounded-lg">
                    <Icon name="AlertTriangle" size={16} className="text-error flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-text-secondary">
                      This will <span className="font-medium text-error">disable</span> the account of{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>. They will no longer be able to log in or access the system.
                    </p>
                  </div>
                )}

                {/* Disable User — OTP step */}
                {actionModal?.type === 'disable-user' && actionResult?.success !== true && actionOtpStep && (
                  <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                      Enter the OTP from authenticator to confirm disabling{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">OTP Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={actionOtp}
                        onChange={(e) => { setActionOtp(e?.target?.value); setActionOtpError(''); }}
                        placeholder="Enter OTP"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          actionOtpError ? 'border-error focus:ring-error/30' : 'border-border'
                        }`}
                      />
                      {actionOtpError && (
                        <p className="mt-1 text-xs text-error flex items-center space-x-1">
                          <Icon name="AlertCircle" size={12} />
                          <span>{actionOtpError}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Unlock User — confirmation step */}
                {actionModal?.type === 'unlock-user' && actionResult?.success !== true && !actionOtpStep && (
                  <div className="flex items-start space-x-3 p-3 bg-success/5 border border-success/20 rounded-lg">
                    <Icon name="LockOpen" size={16} className="text-success flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-text-secondary">
                      This will <span className="font-medium text-success">unlock</span> the account of{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>. They will be able to log in again.
                    </p>
                  </div>
                )}

                {/* Unlock User — OTP step */}
                {actionModal?.type === 'unlock-user' && actionResult?.success !== true && actionOtpStep && (
                  <div className="space-y-3">
                    <p className="text-sm text-text-secondary">
                      Enter the OTP from authenticator to confirm unlocking the account of{' '}
                      <span className="font-medium text-text-primary">
                        {actionModal?.user?.first_name} {actionModal?.user?.last_name}
                      </span>.
                    </p>
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-1">OTP Code</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={actionOtp}
                        onChange={(e) => { setActionOtp(e?.target?.value); setActionOtpError(''); }}
                        placeholder="Enter OTP"
                        className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          actionOtpError ? 'border-error focus:ring-error/30' : 'border-border'
                        }`}
                      />
                      {actionOtpError && (
                        <p className="mt-1 text-xs text-error flex items-center space-x-1">
                          <Icon name="AlertCircle" size={12} />
                          <span>{actionOtpError}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end space-x-3 px-5 py-4 border-t border-border">
                <button
                  onClick={closeActionModal}
                  disabled={actionLoading}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-text-primary hover:bg-muted disabled:opacity-50"
                >
                  {actionResult?.success === true ? 'Close' : 'Cancel'}
                </button>
                {actionResult?.success !== true && (
                  <>
                    {/* Confirmation step buttons for reset-otp, disable-user, and unlock-user */}
                    {(actionModal?.type === 'reset-otp' || actionModal?.type === 'disable-user' || actionModal?.type === 'unlock-user') && !actionOtpStep ? (
                      <button
                        onClick={handleProceedToOtp}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          actionModal?.type === 'disable-user' ? 'bg-error text-white hover:bg-error/90'
                            : actionModal?.type === 'unlock-user'? 'bg-success text-white hover:bg-success/90' :'bg-accent text-white hover:bg-accent/90'
                        }`}
                      >
                        <Icon name="ArrowRight" size={15} />
                        <span>Proceed</span>
                      </button>
                    ) : (
                      <button
                        onClick={
                          actionModal?.type === 'reset-password' ? handleResetPassword :
                          actionModal?.type === 'reset-otp' ? handleResetOtp :
                          actionModal?.type === 'unlock-user' ? handleUnlockUser :
                          handleDisableUser
                        }
                        disabled={actionLoading}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed transition-colors ${
                          actionModal?.type === 'disable-user' ? 'bg-error text-white hover:bg-error/90'
                            : actionModal?.type === 'reset-otp' ? 'bg-accent text-white hover:bg-accent/90'
                            : actionModal?.type === 'unlock-user'? 'bg-success text-white hover:bg-success/90' :'bg-warning text-white hover:bg-warning/90'
                        }`}
                      >
                        {actionLoading ? (
                          <><Icon name="Loader2" size={15} className="animate-spin" /><span>Processing...</span></>
                        ) : (
                          <><Icon name={config?.icon} size={15} /><span>Confirm</span></>
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={handleCloseModal} />
          <div className="relative bg-surface border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="UserPlus" size={18} className="text-primary" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-text-primary">Create New User</h2>
                  <p className="text-xs text-text-secondary">Fill in the details below</p>
                </div>
              </div>
              <button onClick={handleCloseModal} className="p-1.5 rounded-lg hover:bg-muted text-text-secondary">
                <Icon name="X" size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="p-5 space-y-4">
              {successMessage && (
                <div className="flex items-center space-x-2 p-3 bg-success/10 border border-success/20 rounded-lg">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <p className="text-sm text-success font-medium">{successMessage}</p>
                </div>
              )}

              {apiErrors?.length > 0 && (
                <div className="p-3 bg-error/10 border border-error/20 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <Icon name="AlertCircle" size={16} className="text-error" />
                    <p className="text-sm font-medium text-error">Please fix the following errors:</p>
                  </div>
                  <ul className="space-y-1">
                    {apiErrors?.map((err, i) => (
                      <li key={i} className="text-xs text-error flex items-start space-x-1">
                        <span className="mt-0.5">•</span>
                        <span>{err}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <InputField label="First Name" name="first_name" placeholder="Min 2 characters" formData={formData} formErrors={formErrors} onChange={handleInputChange} />
                <InputField label="Middle Name" name="middle_name" placeholder="Min 2 characters" formData={formData} formErrors={formErrors} onChange={handleInputChange} />
              </div>

              <InputField label="Last Name" name="last_name" placeholder="Min 2 characters" formData={formData} formErrors={formErrors} onChange={handleInputChange} />
              <InputField label="Email Address" name="email" type="email" placeholder="user@example.com" formData={formData} formErrors={formErrors} onChange={handleInputChange} />
              <InputField label="Phone Number" name="phone_number" placeholder="10-15 digit number e.g. 254711000127" formData={formData} formErrors={formErrors} onChange={handleInputChange} />

              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">User Type</label>
                {userTypesLoading ? (
                  <div className="flex items-center space-x-2 px-3 py-2 border border-border rounded-lg bg-background">
                    <Icon name="Loader2" size={14} className="text-primary animate-spin" />
                    <span className="text-sm text-text-secondary">Loading types...</span>
                  </div>
                ) : (
                  <select
                    name="user_type"
                    value={formData?.user_type}
                    onChange={handleInputChange}
                    className={`w-full px-3 py-2 border rounded-lg text-sm bg-background text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      formErrors?.user_type ? 'border-error focus:ring-error/30' : 'border-border'
                    }`}
                  >
                    <option value="">Select user type</option>
                    {userTypes?.map(type => (
                      <option key={type?.id} value={type?.id}>
                        {type?.user_type}
                      </option>
                    ))}
                  </select>
                )}
                {formErrors?.user_type && (
                  <p className="mt-1 text-xs text-error flex items-center space-x-1">
                    <Icon name="AlertCircle" size={12} />
                    <span>{formErrors?.user_type}</span>
                  </p>
                )}
              </div>

              <div className="flex items-center justify-end space-x-3 pt-2 border-t border-border">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-border rounded-lg text-sm text-text-primary hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createLoading}
                  className="flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {createLoading ? (
                    <><Icon name="Loader2" size={16} className="animate-spin" /><span>Creating...</span></>
                  ) : (
                    <><Icon name="UserPlus" size={16} /><span>Create User</span></>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
